import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { CheckCircle, AlertCircle, Eye, EyeOff } from 'lucide-react';
import { API_BASE_URL } from '@/lib/api';

const API = API_BASE_URL;
const LOGO_URL = "/logo.png";

export default function RegisterPage() {
  const { code } = useParams();
  const navigate = useNavigate();
  const { guestRegister } = useAuth();
  const [invitation, setInvitation] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [registering, setRegistering] = useState(false);
  const [success, setSuccess] = useState(null);

  useEffect(() => {
    const validate = async () => {
      try {
        const res = await axios.post(`${API}/api/invitations/validate`, { invite_code: code });
        setInvitation(res.data.invitation);
      } catch (e) {
        setError(e.response?.data?.detail || 'Ugyldig invitationskode');
      } finally {
        setLoading(false);
      }
    };
    if (code) validate();
  }, [code]);

  const handleRegister = async (e) => {
    e.preventDefault();
    if (password !== confirmPassword) { setError('Adgangskoderne er ikke ens'); return; }
    if (password.length < 6) { setError('Adgangskoden skal være mindst 6 tegn'); return; }
    setRegistering(true);
    setError('');
    try {
      const data = await guestRegister(code, password);
      setSuccess(data);
    } catch (e) {
      setError(e.response?.data?.detail || 'Registreringen mislykkedes');
    } finally {
      setRegistering(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#020617] flex items-center justify-center">
        <div className="text-slate-400">Validerer invitationskode...</div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen bg-[#020617] flex items-center justify-center px-4">
        <div className="max-w-md w-full animate-fade-in">
          <div className="text-center mb-8">
            <img src={LOGO_URL} alt="Molin30" className="h-12 mx-auto mb-6" />
            <CheckCircle className="w-16 h-16 text-emerald-400 mx-auto mb-4" />
            <h1 className="text-2xl font-semibold text-slate-50 mb-2" style={{ fontFamily: "'Outfit', sans-serif" }}>Velkommen til Molin30!</h1>
            <p className="text-slate-400 text-sm">Din gæstekonto er oprettet.</p>
          </div>
          <div className="bg-slate-800/40 border border-slate-700/50 rounded-xl p-6 mb-6" data-testid="registration-success">
            <div className="space-y-3">
              <div>
                <span className="text-xs text-slate-500 uppercase tracking-wider">E-mail</span>
                <p className="text-blue-400 font-mono text-sm mt-1">{success.email || success.user?.display_email || success.user?.private_email || success.user?.corporate_email}</p>
              </div>
              <div>
                <span className="text-xs text-slate-500 uppercase tracking-wider">Navn</span>
                <p className="text-slate-200 text-sm mt-1">{success.user?.full_name}</p>
              </div>
            </div>
            <p className="text-xs text-slate-500 mt-4 border-t border-slate-700/50 pt-3">
              Brug din private e-mail og adgangskode til at logge ind på portalen.
            </p>
          </div>
          <Button onClick={() => navigate('/portal')} className="w-full bg-blue-600 hover:bg-blue-500 text-white font-medium py-3 rounded-md" data-testid="go-to-portal-btn">
            Gå til portalen
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#020617] flex items-center justify-center px-4">
      <div className="max-w-md w-full animate-fade-in">
        <div className="text-center mb-8">
          <img src={LOGO_URL} alt="Molin30" className="h-12 mx-auto mb-6" />
          <h1 className="text-2xl font-semibold text-slate-50 mb-2" style={{ fontFamily: "'Outfit', sans-serif" }}>
            {invitation ? 'Opret din konto' : 'Ugyldig invitation'}
          </h1>
          {invitation && (
            <p className="text-slate-400 text-sm">Velkommen, {invitation.full_name}. Vælg din adgangskode nedenfor.</p>
          )}
        </div>

        {error && (
          <div className="flex items-center gap-2 bg-red-500/10 border border-red-500/20 rounded-lg px-4 py-3 mb-6" data-testid="register-error">
            <AlertCircle className="w-4 h-4 text-red-400 flex-shrink-0" />
            <span className="text-sm text-red-400">{error}</span>
          </div>
        )}

        {invitation ? (
          <>
            <div className="bg-slate-800/40 border border-slate-700/50 rounded-xl p-5 mb-6">
              <h3 className="text-xs font-bold uppercase tracking-[0.2em] text-blue-400 mb-3">Invitationsdetaljer</h3>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between"><span className="text-slate-400">Navn</span><span className="text-slate-200">{invitation.full_name}</span></div>
                <div className="flex justify-between"><span className="text-slate-400">Kode</span><span className="text-slate-200 font-mono">{invitation.invite_code}</span></div>
                <div className="flex justify-between"><span className="text-slate-400">Ledsager</span><span className="text-slate-200">{invitation.plus_one_allowed ? 'Ja' : 'Nej'}</span></div>
                <div className="flex justify-between"><span className="text-slate-400">Overnatning</span><span className="text-slate-200">{invitation.overnight_allowed ? 'Ja' : 'Nej'}</span></div>
              </div>
            </div>

            <form onSubmit={handleRegister} className="space-y-4" data-testid="register-form">
              <div>
                <label className="text-sm text-slate-400 block mb-1.5">Adgangskode</label>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="w-full bg-slate-900/50 border border-slate-700 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 text-slate-100 rounded-md px-4 py-2.5 outline-none pr-10"
                    data-testid="register-password"
                    required
                  />
                  <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500">
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              <div>
                <label className="text-sm text-slate-400 block mb-1.5">Gentag adgangskode</label>
                <input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="w-full bg-slate-900/50 border border-slate-700 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 text-slate-100 rounded-md px-4 py-2.5 outline-none"
                  data-testid="register-confirm-password"
                  required
                />
              </div>
              <Button type="submit" disabled={registering} className="w-full bg-blue-600 hover:bg-blue-500 text-white font-medium py-3 rounded-md disabled:opacity-50" data-testid="register-submit-btn">
                {registering ? 'Opretter konto...' : 'Opret konto'}
              </Button>
            </form>
          </>
        ) : (
          <div className="text-center">
            <p className="text-slate-400 mb-4">Invitationskoden er ugyldig eller allerede brugt.</p>
            <Button onClick={() => navigate('/')} className="bg-slate-800 hover:bg-slate-700 text-slate-200 border border-slate-700" data-testid="back-to-home-btn">
              Til forsiden
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
